import axios from "axios";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { SubCategoriesData } from "../../../lib/languages";

const DialogDeleteSubCategory = ({
  subCategory,
  category,
  getAllSubCategories,
}) => {
  const { language } = useLanguage();
  const t = SubCategoriesData[language] || SubCategoriesData["en"];

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDeleteCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.delete(
        `${BaseUrlApi}/categories/${category.id}/sub-category/${subCategory.id}`
      );

      toast(t.subCategoryDeleted, {
        description: t.subCategoryDeletedDescription,
      });

      await getAllSubCategories();
      setOpen(false);
    } catch (error) {
      toast(t.error, {
        description: ErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const [confirmText, setConfirmText] = useState("");
  const confirmationWord = "DELETE";
  const isConfirmEnabled = confirmText.toUpperCase() === confirmationWord;

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <DialogTrigger asChild>
        <Button
          size="icon"
          variant="destructive"
          className="rounded-xl h-9"
          onClick={() => {}}
        >
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <DialogTitle className="text-red-500">
              {t.deleteSubCategory} "
              {subCategory.title[language] ||
                subCategory.title.en ||
                subCategory.title.ar ||
                "---"}
              "
            </DialogTitle>
          </div>
          <DialogDescription>
            {t.deleteSubCategoryConfirmation}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-500">
              {t.actionCannotBeUndone}
            </p>
          </div>

          <div className="pl-6">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="bg-background">
                {subCategory.course_count}
              </Badge>
              <span className="text-sm">
                {t.subCategoriesWillBeDeleted}
              </span>
            </div>

            <p className="text-xs text-muted-foreground">
              {t.allAssociatedContentRemoved}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="confirm" className="text-sm font-medium">
            {t.typeDeleteToConfirm}
          </label>
          <Input
            id="confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={
              !isConfirmEnabled
                ? "border-destructive focus-visible:ring-0"
                : "border-green-500 focus-visible:ring-0"
            }
            placeholder={confirmationWord}
          />
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t.Cancel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeleteCategory}
            disabled={loading || !isConfirmEnabled}
            className="gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {t.deleteSubCategory}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDeleteSubCategory;
